import React, { useState, useEffect } from 'react';
import { getBusActivityLogs } from '../../Services/Operations/ProductAPI';
import { toast } from 'react-toastify';
import '../../Styles/BusActivityLog.css';

const BusActivityLogModal = ({ busId, busName, busNumber, onClose, token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const response = await getBusActivityLogs(busId, token, page, 20);
      if (response.success) {
        setLogs(response.data);
        setTotalPages(response.pagination.pages);
        setTotalLogs(response.pagination.total);
        setCurrentPage(page);
      }
    } catch (error) {
      toast.error('Failed to fetch activity logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
  }, [busId]);

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      CREATE: { color: '#28a745', bg: '#d4edda', icon: '✅' },
      UPDATE: { color: '#17a2b8', bg: '#d1ecf1', icon: '📝' },
      DELETE: { color: '#dc3545', bg: '#f8d7da', icon: '🗑️' },
      ACTIVATE: { color: '#20c997', bg: '#d4edda', icon: '🟢' },
      DEACTIVATE: { color: '#ffc107', bg: '#fff3cd', icon: '🔴' },
      APPROVE: { color: '#28a745', bg: '#d4edda', icon: '✅' },
      REJECT: { color: '#dc3545', bg: '#f8d7da', icon: '❌' },
      ASSIGN: { color: '#6f42c1', bg: '#e8d4f8', icon: '🔗' },
      UNASSIGN: { color: '#fd7e14', bg: '#ffe5d0', icon: '🔓' },
    };
    const style = styles[action] || { color: '#6c757d', bg: '#e9ecef', icon: '📋' };
    return (
      <span className="log-action-badge" style={{ 
        backgroundColor: style.bg, 
        color: style.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        {style.icon} {action}
      </span>
    );
  };

  const renderChanges = (changes) => {
    if (!changes) return null;
    
    const fields = changes.fieldsChanged || [];
    if (fields.length === 0) return <span className="no-changes">No field changes</span>;

    return (
      <div className="changes-container">
        <div className="changed-fields">
          <strong>Fields Changed:</strong>
          <div className="field-tags">
            {fields.map((field, idx) => (
              <span key={idx} className="field-tag">{field}</span>
            ))}
          </div>
        </div>
        {changes.summary && (
          <div className="change-summary">
            <strong>Summary:</strong> {changes.summary}
          </div>
        )}
      </div>
    );
  };

  const renderBeforeAfter = (changes) => {
    if (!changes || !changes.before || !changes.after) return null;
    
    const getDisplayValue = (obj, key) => {
      if (!obj) return '—';
      if (key === 'isActive') return obj[key] ? 'Active' : 'Inactive';
      if (key === 'isRoundTrip') return obj[key] ? 'Round Trip' : 'One Way';
      if (key === 'approvalStatus') {
        const status = obj[key] || 'pending';
        return status.charAt(0).toUpperCase() + status.slice(1);
      }
      if (key === 'status') {
        const status = obj[key] || 'inactive';
        return status.charAt(0).toUpperCase() + status.slice(1);
      }
      if (key === 'busType') return obj[key] || 'N/A';
      if (key === 'busCategory') return obj[key] || 'N/A';
      if (key === 'totalSeats') return obj[key] || '0';
      if (key === 'availableSeats') return obj[key] || '0';
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (Array.isArray(obj[key])) {
          return `[${obj[key].length} items]`;
        }
        return JSON.stringify(obj[key], null, 2);
      }
      return obj[key] || '—';
    };

    const fields = changes.fieldsChanged || [];
    if (fields.length === 0) return null;

    return (
      <div className="before-after-container">
        <div className="comparison-grid">
          {fields.slice(0, 10).map((field, idx) => {
            const beforeVal = getDisplayValue(changes.before, field);
            const afterVal = getDisplayValue(changes.after, field);
            const isChanged = beforeVal !== afterVal;
            
            return (
              <div key={idx} className="comparison-row">
                <div className="field-name">{field}</div>
                <div className="before-value">Before: {beforeVal}</div>
                <div className="arrow">→</div>
                <div className={`after-value ${isChanged ? 'changed' : ''}`}>
                  After: {afterVal}
                </div>
              </div>
            );
          })}
        </div>
        {fields.length > 10 && (
          <div className="more-fields">
            + {fields.length - 10} more fields changed
          </div>
        )}
      </div>
    );
  };

  const renderUserInfo = (performedBy) => {
    if (!performedBy) return null;
    return (
      <div className="user-info">
        <span className="user-name">{performedBy.userName}</span>
        <span className="user-type" style={{
          fontSize: '11px',
          color: '#6c757d',
          backgroundColor: '#f0f0f0',
          padding: '2px 8px',
          borderRadius: '10px',
          marginLeft: '8px'
        }}>
          {performedBy.userType}
        </span>
        {performedBy.userEmail && (
          <span className="user-email" style={{ fontSize: '12px', color: '#6c757d', marginLeft: '8px' }}>
            {performedBy.userEmail}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay log-modal-overlay" onClick={onClose}>
      <div className="modal-content log-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header log-modal-header">
          <div className="header-content">
            <h3>Activity Log</h3>
            <span className="route-name-badge">
              {busNumber} - {busName}
            </span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body log-modal-body">
          <div className="log-stats">
            <div className="stat-item">
              <span className="stat-label">Total Activities</span>
              <span className="stat-value">{totalLogs}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page</span>
              <span className="stat-value">{currentPage} of {totalPages}</span>
            </div>
          </div>

          {loading ? (
            <div className="log-loading">
              <div className="spinner"></div>
              <p>Loading activity logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="no-logs">
              <div className="no-logs-icon">📭</div>
              <p>No activity logs found for this bus</p>
            </div>
          ) : (
            <div className="logs-list">
              {logs.map((log) => (
                <div key={log._id} className="log-item">
                  <div className="log-header-row">
                    <div className="log-left">
                      {getActionBadge(log.action)}
                      <span className="log-timestamp">{formatDate(log.createdAt)}</span>
                    </div>
                    <div className="log-right">
                      <button 
                        className="expand-btn"
                        onClick={() => setExpandedLog(expandedLog === log._id ? null : log._id)}
                      >
                        {expandedLog === log._id ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="log-meta">
                    {renderUserInfo(log.performedBy)}
                    <span className="log-version">Version {log.version}</span>
                    {log.ipAddress && (
                      <span className="log-ip">IP: {log.ipAddress}</span>
                    )}
                  </div>

                  {renderChanges(log.changes)}

                  {expandedLog === log._id && (
                    <div className="log-expanded-details">
                      <div className="detail-section">
                        <h5>Before & After Comparison</h5>
                        {renderBeforeAfter(log.changes)}
                      </div>
                      
                      {log.userAgent && (
                        <div className="detail-section">
                          <h5>User Agent</h5>
                          <div className="user-agent-display">{log.userAgent}</div>
                        </div>
                      )}
                      
                      <div className="detail-section">
                        <h5>Full Log Data</h5>
                        <pre className="log-json">
                          {JSON.stringify(log, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && logs.length > 0 && (
            <div className="log-pagination">
              <button 
                className="pagination-btn"
                onClick={() => fetchLogs(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                className="pagination-btn"
                onClick={() => fetchLogs(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-close-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusActivityLogModal;